import JsSIP from 'jssip';

const SIP_WSS = 'wss://webfit.api4com.com:6443';
const SIP_DOMAIN = 'webfit.api4com.com';

let ua = null;
let currentSession = null;
let store = null;
let currentRamal = null;

// Audio element for remote audio playback
let remoteAudio = null;

function getAudioElement() {
  if (!remoteAudio) {
    remoteAudio = document.createElement('audio');
    remoteAudio.id = 'voip-remote-audio';
    remoteAudio.autoplay = true;
    document.body.appendChild(remoteAudio);
  }
  return remoteAudio;
}

export function initSipService(vuexStore) {
  store = vuexStore;
}

export function isRegistered() {
  return ua && ua.isRegistered();
}

export function isConnected() {
  return ua && ua.isConnected();
}

export function register(ramal, senha) {
  return new Promise((resolve, reject) => {
    // Idempotent: if already registered to this ramal, reuse the live UA.
    // Tearing it down here drops the WebSocket mid-call (causa do atraso de 45s).
    if (ua && ua.isRegistered() && currentRamal === ramal) {
      resolve();
      return;
    }
    if (ua) {
      ua.stop();
      ua = null;
    }
    currentRamal = ramal;

    const socket = new JsSIP.WebSocketInterface(SIP_WSS);
    const config = {
      sockets: [socket],
      uri: `sip:${ramal}@${SIP_DOMAIN}`,
      password: senha,
      display_name: 'WebScale',
      register: true,
      session_timers: false,
    };

    try {
      ua = new JsSIP.UA(config);
    } catch (e) {
      reject(e);
      return;
    }

    ua.on('connected', () => {
      if (store) {
        store.commit('voipCalls/SET_SIP_STATE', 'connected');
      }
    });

    ua.on('disconnected', () => {
      if (store) {
        store.commit('voipCalls/SET_SIP_STATE', 'disconnected');
      }
    });

    ua.on('registered', () => {
      if (store) {
        store.commit('voipCalls/SET_SIP_STATE', 'registered');
      }
      resolve();
    });

    ua.on('registrationFailed', ev => {
      if (store) {
        store.commit('voipCalls/SET_SIP_STATE', 'failed');
      }
      reject(new Error(ev.cause || 'Registration failed'));
    });

    ua.on('newRTCSession', ev => {
      // Handle incoming calls
      if (ev.originator === 'remote') {
        handleIncomingCall(ev.session);
      }
    });

    ua.start();
  });
}

// --- Ringback local (tom de "chamando") ---
// O tronco nem sempre envia early media; sem isso a discagem fica MUDA.
// Tom 425Hz, cadência 1s on / 4s off (padrão BR). Para quando o áudio
// remoto chega (early media ou atendimento) ou a chamada termina.
let ringbackCtx = null;
let ringbackInterval = null;

function startRingback() {
  if (ringbackCtx) return;
  try {
    ringbackCtx = new (window.AudioContext || window.webkitAudioContext)();
    const beep = () => {
      if (!ringbackCtx) return;
      const osc = ringbackCtx.createOscillator();
      const gain = ringbackCtx.createGain();
      osc.frequency.value = 425;
      gain.gain.value = 0.06;
      osc.connect(gain);
      gain.connect(ringbackCtx.destination);
      osc.start();
      osc.stop(ringbackCtx.currentTime + 1);
    };
    beep();
    ringbackInterval = setInterval(beep, 5000);
  } catch (e) {
    // AudioContext bloqueado — segue sem tom
  }
}

function stopRingback() {
  if (ringbackInterval) {
    clearInterval(ringbackInterval);
    ringbackInterval = null;
  }
  if (ringbackCtx) {
    try {
      ringbackCtx.close();
    } catch (e) {
      /* já fechado */
    }
    ringbackCtx = null;
  }
}

export function makeCall(phone, contactInfo = {}) {
  if (!ua || !ua.isRegistered()) {
    throw new Error('SIP not registered');
  }

  const options = {
    mediaConstraints: { audio: true, video: false },
    pcConfig: {
      iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
    },
    rtcOfferConstraints: {
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    },
    sessionTimersExpires: 120,
  };

  const target = `sip:${phone}@${SIP_DOMAIN}`;
  currentSession = ua.call(target, options);

  // Cap ICE gathering: esperar a coleta COMPLETA pode travar ~45s (STUN).
  // Manda o INVITE ~2s após o 1o candidato, com o que já coletou (mantém o STUN).
  let iceReadyTimer = null;
  currentSession.on('icecandidate', ({ ready }) => {
    if (iceReadyTimer) return;
    iceReadyTimer = setTimeout(() => {
      try {
        ready();
      } catch (e) {
        /* sessão já avançou/terminou */
      }
    }, 2000);
  });

  // Set active call in store
  if (store) {
    store.commit('voipCalls/SET_ACTIVE_CALL', {
      contact_name: contactInfo.contactName,
      contact_phone: contactInfo.contactPhone,
      contact_id: contactInfo.contactId,
      opportunity_id: contactInfo.opportunityId,
      state: 'dialing',
      startedAt: Date.now(),
    });
  }

  startRingback();
  setupSessionHandlers(currentSession);
  return currentSession;
}

export function hangup() {
  stopRingback();
  if (currentSession) {
    try {
      currentSession.terminate();
    } catch (e) {
      // Session may already be terminated
    }
    currentSession = null;
  }
}

export function toggleMute() {
  if (!currentSession) return false;
  if (currentSession.isMuted().audio) {
    currentSession.unmute({ audio: true });
    return false;
  }
  currentSession.mute({ audio: true });
  return true;
}

export function toggleHold() {
  if (!currentSession) return false;
  if (currentSession.isOnHold().local) {
    currentSession.unhold();
    return false;
  }
  currentSession.hold();
  return true;
}

export function sendDTMF(tone) {
  if (currentSession) {
    currentSession.sendDTMF(tone);
  }
}

export function disconnect() {
  stopRingback();
  if (currentSession) {
    try { currentSession.terminate(); } catch (e) { /* */ }
    currentSession = null;
  }
  if (ua) {
    ua.stop();
    ua = null;
  }
}

// --- Internal ---

function setupSessionHandlers(session) {
  session.on('progress', () => {
    if (store) {
      store.commit('voipCalls/UPDATE_ACTIVE_CALL', { state: 'ringing' });
    }
  });

  session.on('accepted', () => {
    stopRingback();
    if (store) {
      store.commit('voipCalls/UPDATE_ACTIVE_CALL', {
        state: 'in-call',
        answeredAt: Date.now(),
      });
    }
  });

  session.on('ended', ev => {
    stopRingback();
    if (store) {
      store.commit('voipCalls/UPDATE_ACTIVE_CALL', {
        state: 'ended',
        endCause: ev.cause || 'Terminated',
      });
      setTimeout(() => store.commit('voipCalls/CLEAR_ACTIVE_CALL'), 4000);
    }
    currentSession = null;
  });

  session.on('failed', ev => {
    stopRingback();
    if (store) {
      store.commit('voipCalls/UPDATE_ACTIVE_CALL', {
        state: 'failed',
        error: ev.cause || ev.message?.reason || 'Call failed',
      });
      setTimeout(() => store.commit('voipCalls/CLEAR_ACTIVE_CALL'), 5000);
    }
    currentSession = null;
  });

  // Attach remote audio when call connects
  session.on('peerconnection', ev => {
    const pc = ev.peerconnection;

    // Modern API: ontrack
    pc.addEventListener('track', e => {
      if (e.track.kind === 'audio' && e.streams[0]) {
        // Áudio remoto chegou (early media ou atendimento) — para o tom local
        stopRingback();
        const audio = getAudioElement();
        audio.srcObject = e.streams[0];
        audio.play().catch(() => {});
      }
    });

    // Legacy API: onaddstream
    pc.addEventListener('addstream', e => {
      const audio = getAudioElement();
      audio.srcObject = e.stream;
      audio.play().catch(() => {});
    });

    // Extra safety: check for remote streams after connection
    pc.addEventListener('connectionstatechange', () => {
      if (pc.connectionState === 'connected') {
        const receivers = pc.getReceivers();
        const audioReceiver = receivers.find(r => r.track && r.track.kind === 'audio');
        if (audioReceiver) {
          const stream = new MediaStream([audioReceiver.track]);
          const audio = getAudioElement();
          if (!audio.srcObject) {
            audio.srcObject = stream;
            audio.play().catch(() => {});
          }
        }
      }
    });
  });

  // Also try attaching audio when call is accepted (belt and suspenders)
  session.on('accepted', () => {
    setTimeout(() => {
      if (session.connection) {
        const receivers = session.connection.getReceivers();
        const audioReceiver = receivers.find(r => r.track && r.track.kind === 'audio');
        if (audioReceiver) {
          const stream = new MediaStream([audioReceiver.track]);
          const audio = getAudioElement();
          audio.srcObject = stream;
          audio.play().catch(() => {});
        }
      }
    }, 500);
  });
}

function handleIncomingCall(session) {
  // For now, auto-reject incoming calls (we only do outbound)
  // TODO: implement incoming call UI
  session.terminate();
}

export default {
  initSipService,
  register,
  makeCall,
  hangup,
  toggleMute,
  toggleHold,
  sendDTMF,
  disconnect,
  isRegistered,
  isConnected,
};
